import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';

interface TrialBannerProps {
  uid?: string;
  className?: string;
}

/**
 * Displays a compact banner when the user is on a trial.
 * Reads entitlements/{uid}.trialEndsAt and shows remaining days + Upgrade CTA.
 */
const TrialBanner: React.FC<TrialBannerProps> = ({ uid, className }) => {
  const [trialEndsAtMs, setTrialEndsAtMs] = useState<number | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(false);

  useEffect(() => {
    if (!uid) return;
    try {
      const ref = doc(db, 'entitlements', uid);
      const unsub = onSnapshot(ref, (snap) => {
        const data = snap.exists() ? (snap.data() as any) : null;
        const s = data?.status || null;
        setStatus(s);
        const ts = data?.trialEndsAt;
        const date: Date | null = ts?.toDate ? ts.toDate() : (ts ? new Date(ts) : null);
        setTrialEndsAtMs(date ? date.getTime() : null);
      });
      return () => { try { unsub(); } catch {} };
    } catch {}
  }, [uid]);

  const daysLeft = useMemo(() => {
    if (!trialEndsAtMs) return null;
    const now = Date.now();
    const ms = trialEndsAtMs - now;
    if (ms <= 0) return 0;
    return Math.ceil(ms / (24 * 60 * 60 * 1000));
  }, [trialEndsAtMs]);

  if (!uid || dismissed) return null;
  const shouldShow = status === 'trial' && (daysLeft ?? 0) > 0;
  if (!shouldShow) return null;

  return (
    <div className={`rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 ${className || ''}`} role="status" aria-live="polite">
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm text-yellow-900">
          <strong>Trial actief</strong> — nog {daysLeft} {daysLeft === 1 ? 'dag' : 'dagen'} toegang.
        </div>
        <div className="flex items-center gap-2">
          <a href="/#membership" className="inline-flex items-center rounded-md bg-orange-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-600">Upgrade</a>
          <button type="button" aria-label="Sluiten" className="rounded-md border border-yellow-200 bg-white px-2 py-1 text-sm text-yellow-700 hover:bg-yellow-100" onClick={() => setDismissed(true)}>Sluiten</button>
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;

